import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ToggleOff, AttachMoney, Category as CategoryIcon, LocalOffer } from "@mui/icons-material";

import { useGetProducts } from "../../hooks/products/useGetProducts";
import { useDeleteProduct } from "../../hooks/products/useDeleteProduct";
import {
    useBulkUpdateProductsStatus,
    useBulkAdjustProductsPrice,
    useBulkAssignProductsCategory,
    useBulkAssignProductsTag,
} from "../../hooks/products/useBulkProductActions";

import TableLayout from "../global/Table/TableLayout";
import { ProductsTableColumns } from "./Table/ProductsTableColumns";
import ProductsTableRow from "./Table/ProductsTableRow";
import useTableActions from "../../hooks/common/useTableActions";
import { usePermissions } from "../../auth/PermissionsContext";

import BulkActionBar from "../bulk/BulkActionBar";
import BulkResultDialog from "../bulk/BulkResultDialog";
import {
    ProductsBulkStatusDialog,
    ProductsBulkPriceAdjustDialog,
    ProductsBulkAssignCategoryDialog,
    ProductsBulkAssignTagDialog,
} from "./bulk/ProductsBulkDialogs";

const ProductsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const navigate = useNavigate();
    const { has } = usePermissions();
    const canManageProducts = has("products.manage") || has("products.update");

    const { removeProduct, isDeleting } = useDeleteProduct();

    // Bulk product mutations (Backend Sprint 14.1).
    const { mutateAsync: bulkStatus,    isPending: isBulkStatus }    = useBulkUpdateProductsStatus();
    const { mutateAsync: bulkPrice,     isPending: isBulkPrice }     = useBulkAdjustProductsPrice();
    const { mutateAsync: bulkCategory,  isPending: isBulkCategory }  = useBulkAssignProductsCategory();
    const { mutateAsync: bulkTag,       isPending: isBulkTag }       = useBulkAssignProductsTag();
    const isBulkPending = isBulkStatus || isBulkPrice || isBulkCategory || isBulkTag;

    const [bulkDialog, setBulkDialog] = useState(null); // 'status' | 'price' | 'category' | 'tag'
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkRequested, setBulkRequested] = useState(0);

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/products",
        deleteSingle: removeProduct,
    });

    // Filter State
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    const statusOptions = [
        { value: "", label: { ar: "الكل", en: "All" } },
        { value: "active", label: { ar: "نشط", en: "Active" } },
        { value: "draft", label: { ar: "مسودة", en: "Draft" } },
        { value: "archived", label: { ar: "مؤرشف", en: "Archived" } },
    ];

    const typeOptions = [
        { value: "", label: { ar: "الكل", en: "All" } },
        { value: "physical", label: { ar: "مادي", en: "Physical" } },
        { value: "digital", label: { ar: "رقمي", en: "Digital" } },
        { value: "service", label: { ar: "خدمة", en: "Service" } },
    ];

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: statusOptions,
        },
        {
            name: "product_type",
            label: { ar: "نوع المنتج", en: "Product Type" },
            value: typeFilter,
            onChange: (val) => setTypeFilter(val),
            options: typeOptions,
        },
    ];

    const params = useMemo(() => {
        return {
            page,
            per_page: perPage,
            search: search || undefined,
            status: statusFilter !== "" ? statusFilter : undefined,
            product_type: typeFilter !== "" ? typeFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        };
    }, [page, perPage, search, statusFilter, typeFilter, order]);

    const { products, meta, isLoading, error } = useGetProducts(params);

    const runBulk = async (mutate, payload) => {
        setBulkRequested(selectedIds.length);
        try {
            const res = await mutate({ ...payload, ids: selectedIds });
            const data = res?.data || res || {};
            setBulkResult({
                updated: data.updated || [],
                failed: data.failed || [],
            });
        } catch {
            // toast handled by hook
        }
        setBulkDialog(null);
    };

    const bulkActions = canManageProducts
        ? [
            {
                key: 'status',
                label: isArabic ? 'تحديث الحالة' : 'Set status',
                icon: <ToggleOff />,
                disabled: isBulkPending,
                onClick: () => setBulkDialog('status'),
            },
            {
                key: 'price',
                label: isArabic ? 'تعديل السعر' : 'Adjust price',
                icon: <AttachMoney />,
                color: 'warning',
                variant: 'outlined',
                disabled: isBulkPending,
                onClick: () => setBulkDialog('price'),
            },
            {
                key: 'category',
                label: isArabic ? 'تعيين فئة' : 'Assign category',
                icon: <CategoryIcon />,
                color: 'info',
                variant: 'outlined',
                disabled: isBulkPending,
                onClick: () => setBulkDialog('category'),
            },
            {
                key: 'tag',
                label: isArabic ? 'تعيين وسم' : 'Assign tag',
                icon: <LocalOffer />,
                color: 'secondary',
                variant: 'outlined',
                disabled: isBulkPending,
                onClick: () => setBulkDialog('tag'),
            },
        ]
        : [];

    const renderRow = (rowProps) => (
        <ProductsTableRow
            {...rowProps}
            item={rowProps.item}
            isSelected={selectedIds.includes(rowProps.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "المنتجات" : "Products";
    const subtitle = isArabic ? "إدارة مخزون المنتجات وتفاصيلها" : "Manage product inventory and details";

    return (
        <>
            <BulkActionBar
                selectedCount={selectedIds.length}
                actions={bulkActions}
                onClear={() => handleSelectAllClick({ target: { checked: false } }, products)}
            />

            <ProductsBulkStatusDialog
                open={bulkDialog === 'status'}
                count={selectedIds.length}
                isSubmitting={isBulkStatus}
                onClose={() => setBulkDialog(null)}
                onConfirm={(payload) => runBulk(bulkStatus, payload)}
            />
            <ProductsBulkPriceAdjustDialog
                open={bulkDialog === 'price'}
                count={selectedIds.length}
                isSubmitting={isBulkPrice}
                onClose={() => setBulkDialog(null)}
                onConfirm={(payload) => runBulk(bulkPrice, payload)}
            />
            <ProductsBulkAssignCategoryDialog
                open={bulkDialog === 'category'}
                count={selectedIds.length}
                isSubmitting={isBulkCategory}
                onClose={() => setBulkDialog(null)}
                onConfirm={(payload) => runBulk(bulkCategory, payload)}
            />
            <ProductsBulkAssignTagDialog
                open={bulkDialog === 'tag'}
                count={selectedIds.length}
                isSubmitting={isBulkTag}
                onClose={() => setBulkDialog(null)}
                onConfirm={(payload) => runBulk(bulkTag, payload)}
            />

            <BulkResultDialog
                open={Boolean(bulkResult)}
                result={bulkResult}
                totalRequested={bulkRequested}
                onClose={() => setBulkResult(null)}
            />

        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة منتج" : "Add Product"}
            onAddClick={() => navigate("/products/create")}
            isLoading={isLoading}
            error={error}
            items={products}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, products)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            columns={ProductsTableColumns}
            renderRow={renderRow}
            // Menu Actions
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            isDeletingSingle={isDeleting}
            // Filters
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
                setTypeFilter("");
            }}
            emptyLabelAr="لا توجد منتجات حالياً"
            emptyLabelEn="No products found"
        />
        </>
    );
};

export default ProductsTable;
